import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StatCard } from "../components/shared/StatCard";
import { BarChart } from "../components/shared/BarChart";
import { reportsAPI, paymentsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { fmt } from "../components/shared/fmt";
import { useTitle } from "../hooks/useTitle";

const WK = [
	{ d: "Mon", r: 842 },
	{ d: "Tue", r: 1124 },
	{ d: "Wed", r: 978 },
	{ d: "Thu", r: 1456 },
	{ d: "Fri", r: 1892 },
	{ d: "Sat", r: 2145 },
	{ d: "Sun", r: 1234 },
];
const MO = [
	{ m: "Jan", r: 28450 },
	{ m: "Feb", r: 31200 },
	{ m: "Mar", r: 29800 },
	{ m: "Apr", r: 34560 },
	{ m: "May", r: 38900 },
	{ m: "Jun", r: 42100 },
	{ m: "Jul", r: 45600 },
	{ m: "Aug", r: 41200 },
	{ m: "Sep", r: 39800 },
	{ m: "Oct", r: 43200 },
	{ m: "Nov", r: 48900 },
	{ m: "Dec", r: 52400 },
];
const ACT = [
	{
		time: "11:42",
		user: "Omar K.",
		action: "Processed payment",
		detail: "TKN-001234 · 6.750 KWD",
		color: "#06a384",
	},
	{
		time: "11:38",
		user: "Fatima N.",
		action: "New order opened",
		detail: "T8 · 3 items",
		color: "#3b82f6",
	},
	{
		time: "11:30",
		user: "Omar K.",
		action: "Deal applied",
		detail: "LOYAL code — 10% off",
		color: "#f59e0b",
	},
	{
		time: "11:18",
		user: "Sara M.",
		action: "Menu item disabled",
		detail: "Veggie Burger → unavailable",
		color: "#a855f7",
	},
	{
		time: "11:05",
		user: "Ahmed A.",
		action: "Admin login",
		detail: "Admin panel access",
		color: "#ec4899",
	},
	{
		time: "10:54",
		user: "Khalid A.",
		action: "Order voided",
		detail: "TKN-001229 · Mgr approval",
		color: "#ef4444",
	},
	{
		time: "10:40",
		user: "Fatima N.",
		action: "Table reserved",
		detail: "T4 — Ali Hassan, 7pm",
		color: "#0891b2",
	},
	{
		time: "10:12",
		user: "Ahmed A.",
		action: "New deal created",
		detail: "FAM01 — Family Meal",
		color: "#f59e0b",
	},
];
const PM_LIST = [
	{ id: "cash", l: "Cash", i: "💵", c: "#06a384" },
	{ id: "knet", l: "KNET", i: "🏧", c: "#3b82f6" },
	{ id: "visa", l: "Visa", i: "💳", c: "#1d4ed8" },
	{ id: "master", l: "Mastercard", i: "💳", c: "#b91c1c" },
	{ id: "amex", l: "Amex", i: "💳", c: "#0369a1" },
	{ id: "qr", l: "QR Pay", i: "📱", c: "#7c3aed" },
];

export const DashboardPage: React.FC = () => {
	useTitle("Dashboard");
	const { user } = useAuth();
	const [period, setPeriod] = useState<"week" | "month">("week");
	const { data: daily } = useQuery({
		queryKey: ["daily"],
		queryFn: () => reportsAPI.daily(),
		retry: false,
	});
	const { data: summary } = useQuery({
		queryKey: ["paysum"],
		queryFn: () => paymentsAPI.getSummary(),
		retry: false,
	});
	const dr = daily?.data || {};
	const pm = summary?.data?.byMethod || {};

	return (
		<div>
			<div className="page-title">Dashboard</div>
			<div className="page-sub">
				Welcome back, {user?.name.split(" ")[0]} ·{" "}
				{new Date().toLocaleDateString("en-KW", {
					weekday: "long",
					year: "numeric",
					month: "long",
					day: "numeric",
				})}
			</div>

			<div className="stat-grid">
				{[
					{
						label: "Today's Revenue",
						value: fmt(dr.totalRevenue || 7270.25),
						cls: "green",
						icon: "💰",
						change: "+12.4% vs yesterday",
						trend: "up" as const,
						accent: "#06a384",
					},
					{
						label: "Orders Today",
						value: dr.totalOrders || 197,
						cls: "",
						icon: "📋",
						change: "+8 vs yesterday",
						trend: "up" as const,
						accent: "#3b82f6",
					},
					{
						label: "Avg Order",
						value: fmt(dr.avgOrder || 36.9),
						cls: "",
						icon: "📊",
						change: "+2.1%",
						trend: "up" as const,
						accent: "#a855f7",
					},
					{
						label: "Monthly Revenue",
						value: fmt(52400),
						cls: "green",
						icon: "📈",
						change: "+7.2% vs Nov",
						trend: "up" as const,
						accent: "#06a384",
					},
					{
						label: "VAT Collected",
						value: fmt(dr.totalTax || 946.59),
						cls: "amber",
						icon: "🏛️",
						change: "15% rate",
						accent: "#f59e0b",
					},
					{
						label: "Voided Orders",
						value: dr.voidedOrders || 2,
						cls: "red",
						icon: "❌",
						change: "Require review",
						accent: "#ef4444",
					},
				].map((s) => (
					<StatCard key={s.label} {...s} />
				))}
			</div>

			<div className="grid-2">
				<div className="card">
					<div className="card-title">
						Revenue Trend
						<div className="filter-tabs" style={{ marginBottom: 0 }}>
							{(["week", "month"] as const).map((p) => (
								<button
									key={p}
									className={`filter-tab ${period === p ? "active" : ""}`}
									onClick={() => setPeriod(p)}>
									{p === "week" ? "This Week" : "This Year"}
								</button>
							))}
						</div>
					</div>
					<BarChart
						data={period === "week" ? WK : MO}
						valueKey="r"
						labelKey={period === "week" ? "d" : "m"}
						color="#06a384"
					/>
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							marginTop: 12,
							fontSize: 12,
							color: "var(--muted)",
						}}>
						<span>
							Total:{" "}
							<strong className="text-accent">
								{fmt(
									(period === "week" ? WK : MO).reduce((s, d) => s + d.r, 0),
								)}
							</strong>
						</span>
						<span>
							Best:{" "}
							<strong className="text-amber">
								{period === "week" ? "Saturday" : "December"}
							</strong>
						</span>
					</div>
				</div>

				<div className="card">
					<div className="card-title">Activity Log</div>
					{ACT.map((a, i) => (
						<div key={i} className="act-row">
							<span className="act-time">{a.time}</span>
							<div className="act-dot" style={{ background: a.color }} />
							<div>
								<div>
									<span
										style={{
											fontSize: 11,
											fontWeight: 700,
											color: "var(--text2)",
										}}>
										{a.user}
									</span>{" "}
									<span style={{ fontSize: 13 }}>{a.action}</span>
								</div>
								<div
									style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>
									{a.detail}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>

			<div className="card">
				<div className="card-title">Today's Payment Breakdown</div>
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
						gap: 10,
					}}>
					{PM_LIST.map((m) => {
						const md = pm[m.id] || { amount: 0, count: 0 };
						return (
							<div
								key={m.id}
								style={{
									background: "var(--card2)",
									borderRadius: "var(--rs)",
									padding: 14,
									border: "1px solid var(--border)",
								}}>
								<div
									style={{
										display: "flex",
										alignItems: "center",
										gap: 8,
										marginBottom: 8,
									}}>
									<span style={{ fontSize: 20 }}>{m.i}</span>
									<span style={{ fontSize: 12, fontWeight: 700 }}>{m.l}</span>
								</div>
								<div
									style={{
										fontFamily: "Syne,sans-serif",
										fontSize: 16,
										fontWeight: 800,
										color: m.c,
									}}>
									{fmt(md.amount || 0)}
								</div>
								<div
									style={{ fontSize: 11, color: "var(--muted)", marginTop: 3 }}>
									{md.count || 0} transactions
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
};
